import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface WizardLayoutProps {
  currentStep: number;
  totalSteps: number;
  children: React.ReactNode;
  onNext: () => void;
  onPrevious: () => void;
  onStepClick: (step: number) => void;
  canProceed: boolean;
  isLastStep: boolean;
  isSubmitting?: boolean;
  timeline?: string;
  onClose?: () => void;
}

const WizardLayout: React.FC<WizardLayoutProps> = ({
  currentStep,
  totalSteps,
  children,
  onNext,
  onPrevious,
  onStepClick,
  canProceed,
  isLastStep,
  isSubmitting = false,
  timeline,
  onClose,
}) => {
  const allSteps = [
    'Location & Info',
    'Question 1',
    'Question 2',
    'Question 3',
    'Question 4',
    'Question 5',
    'Question 6',
    'Question 7',
    'Question 8',
    'Question 9',
    'Review & Submit',
  ];

  // Filter steps based on timeline
  const getValidSteps = () => {
    if (timeline === 'vote_count') {
      // For vote count timeline, only show steps 1, 2, 3, 4, and 11
      return [
        { index: 0, label: allSteps[0] }, // Step 1: Location & Info
        { index: 1, label: allSteps[1] }, // Step 2: Question 1
        { index: 2, label: allSteps[2] }, // Step 3: Question 2
        { index: 3, label: allSteps[3] }, // Step 4: Question 3
        { index: 10, label: allSteps[10] }, // Step 11: Review & Submit
      ];
    }
    // For other timelines, show all steps
    return allSteps.map((label, index) => ({ index, label }));
  };

  const validSteps = getValidSteps();

  // Map current step to display position for vote count
  const getDisplayStep = (step: number) => {
    if (timeline === 'vote_count') {
      const stepMapping = { 1: 1, 2: 2, 3: 3, 4: 4, 11: 5 };
      return stepMapping[step as keyof typeof stepMapping] || step;
    }
    return step;
  };

  const displayStep = getDisplayStep(currentStep);
  const displayTotalSteps = validSteps.length;

  return (
    <div className="max-w-4xl px-4 mx-auto sm:px-6 lg:px-8">
      {/* Header with close button */}
      {onClose && (
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">E-Day Report Form</h1>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-3 py-2 text-white transition-colors duration-200 bg-red-500 rounded-lg hover:bg-red-600"
            title="Close form"
          >
            <XMarkIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Close</span>
          </button>
        </div>
      )}

      {/* Modern Progress Indicator */}
      <div className="mb-8">
        {/* Step Progress - Modern Design */}
        <div className="p-6 mb-6 bg-white border border-gray-100 shadow-sm rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Progress Overview</h2>
              <p className="text-sm text-gray-600">Step {displayStep} of {displayTotalSteps} • {Math.round((displayStep / displayTotalSteps) * 100)}% Complete</p>
            </div>
            {timeline === 'vote_count' && (
              <div className="px-3 py-1 text-xs font-medium text-orange-800 bg-orange-100 rounded-full">
                Vote Count Mode
              </div>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="w-full h-3 bg-gray-200 rounded-full">
              <div
                className="relative h-3 overflow-hidden transition-all duration-500 ease-out rounded-full bg-gradient-to-r from-blue-500 to-blue-600"
                style={{ width: `${(displayStep / displayTotalSteps) * 100}%` }}
              >
                <div className="absolute inset-0 bg-white bg-opacity-20 animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Step Indicators - Modern Design */}
          <div className="flex items-center justify-between pb-2 overflow-x-auto">
            {validSteps.map((step, displayIndex) => {
              const actualStepNumber = step.index + 1;
              const displayStepNumber = displayIndex + 1;
              const isActive = actualStepNumber === currentStep;
              const isCompleted = actualStepNumber < currentStep || (timeline === 'vote_count' && currentStep > 4 && displayIndex < 4);
              const isClickable = actualStepNumber <= currentStep || (timeline === 'vote_count' && currentStep >= 11 && displayIndex === 4);

              return (
                <div key={displayIndex} className="flex flex-col items-center flex-1 min-w-0">
                  <button
                    onClick={() => isClickable && onStepClick(actualStepNumber)}
                    disabled={!isClickable}
                    className={`
                      w-10 h-10 rounded-xl flex items-center justify-center text-sm font-semibold mb-2
                      transition-all duration-300 transform hover:scale-105
                      ${isCompleted
                        ? 'bg-gradient-to-br from-green-500 to-green-600 text-white shadow-lg shadow-green-500/25'
                        : isActive
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/25 ring-4 ring-blue-500/20'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}
                      ${isClickable ? 'cursor-pointer' : 'cursor-not-allowed'}
                    `}
                    title={step.label}
                  >
                    {isCompleted ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    ) : displayStepNumber}
                  </button>
                  <div className={`text-center px-2 ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                    <div className={`text-xs font-medium ${isActive ? 'text-blue-600' : 'text-gray-600'}`}>
                      {step.label.split(':')[0]}
                    </div>
                    {step.label.includes(':') && (
                      <div className="hidden mt-1 text-xs leading-tight text-gray-400 sm:block">
                        {step.label.split(':')[1]?.trim().substring(0, 20)}
                        {step.label.split(':')[1]?.trim().length > 20 ? '...' : ''}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mb-6 card">
        {children}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onPrevious}
          disabled={currentStep === 1}
          className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>

        <div className="text-sm text-gray-500">
          {displayStep} / {displayTotalSteps}
          {!canProceed && (
            <div className="mt-1 text-xs text-red-500">
              Complete all required fields
            </div>
          )}
        </div>

        {isLastStep ? (
          <button
            onClick={onNext}
            disabled={!canProceed || isSubmitting}
            className="btn btn-success disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </button>
        ) : (
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        )}
      </div>
    </div>
  );
};

export default WizardLayout;